import BaseSelect from 'components/Selects/BaseSelect/BaseSelect';
import { findSelectOption } from 'components/Selects/BaseSelect/utils/baseSelect.utils';
import { useEffect, useMemo, useState } from 'react';
import { Button } from 'react-bootstrap';
import { PiWarningFill } from 'react-icons/pi';
import { usePerms } from 'contexts/PermsContext';
import { ACTION_CATEGORY_PERMISSIONS } from '../../../config/globalActionPermissions.config';
import {
  actionRowPermissionNotice,
  canUseActionCategory,
} from '../../../utils/actionPermissions.utils';
import { builderComponentRegistry } from '../utils/builderComponentRegistry.utils';
import { getSectionCategoryOptions } from '../utils/builderForm.utils';
import { FaTrash } from 'react-icons/fa6';

const BuilderRow = ({
  section,
  label,
  item,
  readOnly,
  onUpdate,
  onRemove,
  validationMessage,
  hideCategorySelector = false,
  hideRemoveButton = false,
}) => {
  const [editorError, setEditorError] = useState(null);
  const perms = usePerms();
  // The backend permission middleware refuses an action category that the user may not use
  // (config/globalActionPermissions.config.ts). The select does not offer it.
  const categoryOptions = useMemo(
    () =>
      getSectionCategoryOptions(section).filter((category) =>
        canUseActionCategory(perms, category),
      ),
    [perms, section],
  );
  // An existing row of such a category stays visible, but read-only, with a notice.
  const deniedPermission =
    item.category && !canUseActionCategory(perms, item.category)
      ? ACTION_CATEGORY_PERMISSIONS[item.category].permission
      : null;
  const rowReadOnly = Boolean(readOnly || deniedPermission);
  const categorySelectOptions = useMemo(
    () => categoryOptions.map((option) => ({ value: option, label: option })),
    [categoryOptions],
  );
  const config = item.category ? builderComponentRegistry[item.category] : null;
  const showWarning = !rowReadOnly && Boolean(validationMessage || editorError?.message);
  const errorMessage = rowReadOnly ? '' : validationMessage || editorError?.message || '';

  useEffect(() => {
    setEditorError(null);
  }, [item.id, item.category]);

  return (
    <div className="d-flex flex-column gap-3">
      <div
        className={`d-flex flex-column flex-lg-row align-items-start align-items-lg-center gap-3 ${
          label ? 'justify-content-between' : 'justify-content-lg-end'
        }`}
      >
        {label ? (
          <div className="fs-5 fw-bold d-flex align-items-center gap-2">
            <span>{label}</span>
            {showWarning && <PiWarningFill className="text-warning" />}
          </div>
        ) : (
          showWarning && <PiWarningFill className="text-warning fs-5" />
        )}

        {!rowReadOnly && !hideRemoveButton && (
          <Button
            variant="outline-light"
            className="align-self-start"
            aria-label={`Remove ${label || 'builder row'}`}
            onClick={onRemove}
          >
            <FaTrash />
          </Button>
        )}
      </div>

      {rowReadOnly || hideCategorySelector ? (
        <div className="global-action-builder-readonly-value w-100 rounded-2 px-3 py-2">
          {item.category || '-Select-'}
        </div>
      ) : (
        <BaseSelect
          isSearchable
          maxMenuHeight={240}
          classNames={{ control: () => 'global-action-builder-field' }}
          selectProps={{
            'aria-label': label || 'Category',
            options: categorySelectOptions,
            value: findSelectOption(categorySelectOptions, item.category),
            placeholder: '-Select-',
            onChange: (option) => {
              const category = option?.value ?? '';
              onUpdate({
                category,
                formData: builderComponentRegistry[category]?.createInitialFormData?.() ?? {},
              });
            },
          }}
        />
      )}

      {errorMessage &&
        (Array.isArray(errorMessage) ? (
          <div className="text-warning small" role="alert">
            {errorMessage.length > 3 ? (
              `Multiple issues need attention (${errorMessage.length}). Complete the required IPAWS fields to enable saving.`
            ) : (
              <>
                <span>Complete these IPAWS fields:</span>
                <ul className="mb-0 ps-4">
                  {errorMessage.map((issue) => (
                    <li key={issue}>{issue}</li>
                  ))}
                </ul>
              </>
            )}
          </div>
        ) : (
          <div className="text-warning small" role="alert">
            {errorMessage}
          </div>
        ))}

      {deniedPermission && (
        <div
          className="text-warning small"
          role="alert"
          data-testid="builder-row-permission-notice"
        >
          {actionRowPermissionNotice(deniedPermission)}
        </div>
      )}

      <div
        className="global-action-builder-editor w-100 rounded-3 p-3 p-lg-4"
        style={{
          minHeight: config ? 96 : 0,
        }}
      >
        {config &&
          (rowReadOnly ? (
            <fieldset disabled style={{ border: 'none', margin: 0, padding: 0, width: '100%' }}>
              {config.render({
                setData: (formData) => onUpdate({ formData }),
                data: item.formData ?? {},
                setError: setEditorError,
                readOnly: rowReadOnly,
              })}
            </fieldset>
          ) : (
            config.render({
              setData: (formData) => onUpdate({ formData }),
              data: item.formData ?? {},
              setError: setEditorError,
              readOnly,
            })
          ))}
      </div>
    </div>
  );
};

export default BuilderRow;
